/**
 * EmailTemplateGuideModal — guida integrata all'editor testi email
 * e alle traduzioni AI (req. Fabrizio 13/07/2026: guida nel portale,
 * non come PDF esterno).
 */
import { X, BookOpen, FileEdit, Languages, ShieldCheck, Globe2 } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <div className="border border-gray-800 rounded-xl p-4 bg-[#0d0d0d]">
    <h4 className="text-white text-sm font-semibold flex items-center gap-2 mb-2">
      <Icon size={14} className="text-lojack-blue-400" /> {title}
    </h4>
    <div className="text-gray-400 text-xs leading-relaxed space-y-1.5">{children}</div>
  </div>
);

export const EmailTemplateGuideModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="template-guide-modal">
    <div className="bg-[#0a0a0a] border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-gray-800">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <BookOpen size={16} className="text-lojack-blue-400" />
          Guida — Modifica testi email
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="template-guide-close">
          <X size={16} />
        </button>
      </div>

      <div className="p-5 overflow-y-auto flex-1 space-y-3">
        <Section icon={FileEdit} title="1 · Cosa puoi modificare">
          <p>
            Puoi personalizzare i testi delle <strong className="text-white">3 email che il cliente riceve nel funnel</strong>:
            benvenuto, codice OTP di firma e conferma contratto firmato. Ogni testo è modificabile
            <strong className="text-white"> per singola lingua</strong>, selezionandola dal menu "Paese del destinatario".
          </p>
          <p>
            I campi lasciati <strong className="text-white">vuoti</strong> mantengono il testo di default della piattaforma
            (mostrato in grigio come suggerimento): non è mai necessario compilare tutto.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="2 · Regole d'oro">
          <p>
            • Il segnaposto <code className="text-lojack-blue-300 bg-black px-1 rounded">{"{name}"}</code>, dove presente,
            viene sostituito automaticamente con il nome del cliente: <strong className="text-white">non cancellarlo</strong> e
            non aggiungere altre parentesi graffe (il sistema blocca comunque i testi non validi).
          </p>
          <p>
            • Le modifiche valgono <strong className="text-white">sia per le email reali sia per l'anteprima</strong>:
            dopo il salvataggio l'anteprima a destra si aggiorna da sola.
          </p>
          <p>
            • "<strong className="text-white">Ripristina default</strong>" cancella le personalizzazioni della lingua
            corrente e torna ai testi ufficiali. Ogni modifica è tracciata nell'audit log (chi, cosa, quando).
          </p>
        </Section>

        <Section icon={Languages} title="3 · Traduzioni con AI">
          <p>
            Dopo aver salvato i testi in una lingua (es. italiano), compare il bottone
            "<strong className="text-white">Proponi traduzioni (AI)</strong>":
          </p>
          <p>1. Scegli le lingue di destinazione e clicca <em>Genera proposte</em></p>
          <p>2. L'AI prepara le traduzioni preservando {"{name}"}, la formattazione e i nomi dei prodotti (LoJack, Apple/Google Wallet)</p>
          <p>3. <strong className="text-white">Rivedi ogni lingua</strong>, correggi se serve, e salva una per una con "Salva testi"</p>
          <p className="text-lojack-clause-200">
            ⚠ Nulla viene salvato in automatico: senza la tua revisione e il tuo clic su Salva, ai clienti continua
            ad arrivare il testo precedente.
          </p>
        </Section>

        <Section icon={Globe2} title="4 · Lingue condivise tra Paesi">
          <p>
            Alcuni Paesi condividono il pacchetto lingua: es. <strong className="text-white">Svizzera → tedesco</strong>,
            <strong className="text-white"> Belgio → francese</strong>, <strong className="text-white">Irlanda/Gibilterra → inglese</strong>.
            Modificando la lingua "madre" la modifica vale per tutti i Paesi che la usano — quando succede,
            l'editor lo segnala con un avviso.
          </p>
        </Section>
      </div>

      <div className="p-4 border-t border-gray-800 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white rounded-xl text-sm font-medium transition"
          data-testid="template-guide-ok"
        >
          Ho capito
        </button>
      </div>
    </div>
  </div>
);

export default EmailTemplateGuideModal;
